import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronRight, Check, Shield, BookOpen, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactModal from '../components/ContactModal';
import '../styles/Home.css';

const CAL_URL =
  'https://www.cal.eu/jaredtalbot/30min?user=jaredtalbot&overlayCalendar=true';

const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const emailInputRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const focusEmailField = (e) => {
    if (e) e.preventDefault();
    setMobileMenuOpen(false);
    const el = document.getElementById('hero');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    // Give scrolling a moment, then move keyboard focus to the input
    setTimeout(() => {
      if (emailInputRef.current) {
        emailInputRef.current.focus({ preventScroll: true });
      }
    }, 450);
  };

  const openContactModal = () => {
    setMobileMenuOpen(false);
    setContactModalOpen(true);
  };

  const year = new Date().getFullYear();

  return (
    <div className="gybtc-container">
      <header className={`gybtc-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="gybtc-header-content">
          <Link to="/" className="gybtc-logo-container" aria-label="GrowYourBTC home">
            <img
              src="/gybtc-logo.png"
              alt="GrowYourBTC"
              className="gybtc-logo"
            />
          </Link>

          <nav
            className={`gybtc-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}
            aria-label="Primary"
          >
            <button
              onClick={focusEmailField}
              className="gybtc-nav-link"
            >
              Free Guide
            </button>
            <button
              onClick={() => scrollToSection('how-it-helps')}
              className="gybtc-nav-link"
            >
              How It Helps
            </button>
            <button
              onClick={() => scrollToSection('about-jared')}
              className="gybtc-nav-link"
            >
              About Jared
            </button>
            <button
              onClick={() => scrollToSection('call')}
              className="gybtc-nav-link"
            >
              Free 30-Minute Call
            </button>
            <button onClick={openContactModal} className="gybtc-nav-link">
              Contact
            </button>
          </nav>

          <button
            className="gybtc-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="primary-nav"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* ===================== HERO ===================== */}
      <section id="hero" className="gybtc-hero">
        <div className="gybtc-hero-bg-watermark" aria-hidden="true">
          <span className="gybtc-hero-btc-mark">₿</span>
        </div>

        <div className="gybtc-hero-content">
          <p className="gybtc-hero-eyebrow">Free Bitcoin Education Guide</p>

          <h1 className="gybtc-hero-title">You Never Owned Your Money</h1>

          <p className="gybtc-hero-subtitle">
            A plain-English starting point for understanding money, ownership
            and where Bitcoin fits.
          </p>

          <p className="gybtc-hero-positioning">
            GrowYourBTC helps beginners understand Bitcoin, learn about
            self-custody and build safer habits — one step at a time.
          </p>

          <form
            id="newsletter-form"
            className="gybtc-hero-form"
            noValidate
            aria-label="Get the free guide"
          >
            <label htmlFor="newsletter-email" className="gybtc-visually-hidden">
              Email address
            </label>
            <input
              id="newsletter-email"
              ref={emailInputRef}
              type="email"
              name="email"
              placeholder="Enter your email address"
              required
              autoComplete="email"
              className="gybtc-hero-input"
            />
            <button
              type="submit"
              className="gybtc-btn-primary gybtc-hero-submit"
            >
              Get the free PDF
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </form>

          <div
            id="newsletter-toast"
            role="status"
            aria-live="polite"
            style={{ display: 'none' }}
          ></div>

          <p className="gybtc-hero-context">
            Your email also joins the GrowYourBTC newsletter. You’ll receive a
            confirmation email from MailerLite — confirm it to receive the
            guide.
          </p>
          <p className="gybtc-hero-privacy">
            We handle your details per our{' '}
            <Link to="/privacy" className="gybtc-inline-link">
              Privacy Policy
            </Link>
            . No spam — just practical insights and occasional updates.
          </p>

          <p className="gybtc-hero-secondary">
            Prefer a conversation?{' '}
            <button
              type="button"
              onClick={() => scrollToSection('call')}
              className="gybtc-text-link"
            >
              Book a free 30-minute call.
            </button>
          </p>
        </div>
      </section>

      {/* ============ START WITH A CLEARER UNDERSTANDING ============ */}
      <section id="guide-preview" className="gybtc-guide-preview">
        <div className="gybtc-guide-preview-content">
          <h2 className="gybtc-section-title">
            Start with a clearer understanding
          </h2>
          <p className="gybtc-section-intro">
            Explore the questions behind money, savings and ownership — and
            where Bitcoin enters the conversation.
          </p>

          <ul className="gybtc-guide-list">
            <li className="gybtc-guide-item">
              <span className="gybtc-guide-check" aria-hidden="true">
                <Check size={18} strokeWidth={3} />
              </span>
              <span>Why money feels like a leaking bucket</span>
            </li>
            <li className="gybtc-guide-item">
              <span className="gybtc-guide-check" aria-hidden="true">
                <Check size={18} strokeWidth={3} />
              </span>
              <span>The hidden system most people never question</span>
            </li>
            <li className="gybtc-guide-item">
              <span className="gybtc-guide-check" aria-hidden="true">
                <Check size={18} strokeWidth={3} />
              </span>
              <span>A simple path toward real ownership</span>
            </li>
            <li className="gybtc-guide-item">
              <span className="gybtc-guide-check" aria-hidden="true">
                <Check size={18} strokeWidth={3} />
              </span>
              <span>Where Bitcoin fits into all of this</span>
            </li>
          </ul>
        </div>
      </section>

      {/* ============ LEARN · OWN · PROTECT ============ */}
      <section id="how-it-helps" className="gybtc-pillars">
        <div className="gybtc-section-header">
          <h2 className="gybtc-section-title">Learn. Own. Protect.</h2>
          <p className="gybtc-section-intro">
            You don’t need to understand everything at once. Start with the
            basics and build confidence at your own pace.
          </p>
        </div>

        <div className="gybtc-pillars-grid">
          <article className="gybtc-pillar-card">
            <div className="gybtc-pillar-icon" aria-hidden="true">
              <BookOpen size={28} />
            </div>
            <h3 className="gybtc-pillar-title">Learn</h3>
            <p className="gybtc-pillar-text">
              Understand Bitcoin in plain English, ask questions and get
              comfortable with the fundamentals.
            </p>
          </article>

          <article className="gybtc-pillar-card">
            <div className="gybtc-pillar-icon" aria-hidden="true">
              <Lock size={28} />
            </div>
            <h3 className="gybtc-pillar-title">Own</h3>
            <p className="gybtc-pillar-text">
              Learn what wallets, private keys and self-custody mean — and the
              responsibilities that come with holding your own Bitcoin.
            </p>
          </article>

          <article className="gybtc-pillar-card">
            <div className="gybtc-pillar-icon" aria-hidden="true">
              <Shield size={28} />
            </div>
            <h3 className="gybtc-pillar-title">Protect</h3>
            <p className="gybtc-pillar-text">
              Understand backups, common scams and careful habits that help
              reduce avoidable mistakes.
            </p>
          </article>
        </div>
      </section>

      {/* ============ MEET JARED ============ */}
      <section id="about-jared" className="gybtc-jared">
        <div className="gybtc-jared-grid">
          <div className="gybtc-jared-photo-wrap">
            <img
              src="/jared-cutout.png"
              alt="Jared, the person behind GrowYourBTC."
              className="gybtc-jared-photo"
              width="560"
              height="598"
              loading="lazy"
            />
          </div>

          <div className="gybtc-jared-body">
            <p className="gybtc-section-eyebrow">About</p>
            <h2 className="gybtc-section-title gybtc-section-title--left">
              Hi, I’m Jared.
            </h2>
            <p className="gybtc-jared-text">
              I’m the person behind GrowYourBTC. My aim is to make Bitcoin
              easier to understand, especially when you’re starting out. That
              means clear explanations, space for questions and practical
              education about ownership and security.
            </p>
          </div>
        </div>
      </section>

      {/* ============ FREE 30-MINUTE CALL ============ */}
      <section id="call" className="gybtc-call">
        <div className="gybtc-call-content">
          <p className="gybtc-section-eyebrow">Personal help, at your pace</p>
          <h2 className="gybtc-section-title">New to Bitcoin? Let’s talk.</h2>
          <p className="gybtc-call-text">
            If you’re unsure where to begin, book a free 30-minute Bitcoin
            education call with me. We can talk through the basics, wallets,
            self-custody or questions you’re finding confusing.
          </p>
          <p className="gybtc-call-text gybtc-call-text--muted">
            No sales pitch. No trading signals. Just a conversation and a
            clearer understanding of your next steps.
          </p>

          <a
            href={CAL_URL}
            className="gybtc-btn-outline gybtc-call-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a free 30-minute call
            <ChevronRight size={18} aria-hidden="true" />
          </a>
          <p className="gybtc-call-meta">
            30 minutes · Free · Online via Cal Video
          </p>

          <div className="gybtc-call-reassure" role="note">
            Your Bitcoin stays under your control. Never share your recovery
            phrase, private keys or passwords. I won’t ask you to send Bitcoin
            or hand over control of your wallet.
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section id="faq-home" className="gybtc-faq">
        <div className="gybtc-faq-content">
          <p className="gybtc-section-eyebrow">FAQ</p>
          <h2 className="gybtc-section-title">A few quick questions</h2>

          <div className="gybtc-faq-list">
            <details className="gybtc-faq-item">
              <summary className="gybtc-faq-q">
                Do I need to know anything about Bitcoin?
                <span className="gybtc-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-faq-a">
                No. The guide and call are intended to give beginners a clear
                place to start.
              </p>
            </details>

            <details className="gybtc-faq-item">
              <summary className="gybtc-faq-q">
                Is the call really free?
                <span className="gybtc-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-faq-a">
                Yes. The call is free and lasts 30 minutes. There is no
                obligation to buy anything.
              </p>
            </details>

            <details className="gybtc-faq-item">
              <summary className="gybtc-faq-q">
                Do I need to own Bitcoin?
                <span className="gybtc-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-faq-a">
                No. You can start by learning and asking questions.
              </p>
            </details>

            <details className="gybtc-faq-item">
              <summary className="gybtc-faq-q">
                Will you tell me what to buy?
                <span className="gybtc-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-faq-a">
                No. GrowYourBTC provides education, not personalised financial
                advice or trading recommendations.
              </p>
            </details>

            <details className="gybtc-faq-item">
              <summary className="gybtc-faq-q">
                Will you need access to my wallet?
                <span className="gybtc-faq-icon" aria-hidden="true">+</span>
              </summary>
              <p className="gybtc-faq-a">
                No. Keep your recovery phrase, private keys and passwords
                private. You stay in control of your funds.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* ============ FINAL INVITATION ============ */}
      <section id="final-invite" className="gybtc-final-invite">
        <div className="gybtc-final-invite-content">
          <h2 className="gybtc-section-title">Start with the free guide.</h2>
          <button
            type="button"
            onClick={focusEmailField}
            className="gybtc-btn-primary gybtc-final-cta"
          >
            Get the free PDF
            <ChevronRight size={20} aria-hidden="true" />
          </button>
          <p className="gybtc-final-secondary">
            Or{' '}
            <a
              href={CAL_URL}
              className="gybtc-text-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              book a free 30-minute call
            </a>
            .
          </p>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer id="footer" className="gybtc-footer">
        <div className="gybtc-footer-content">
          <p className="gybtc-footer-disclaimer">
            Bitcoin education only. Nothing on this site or in a call is
            personalised financial advice. Bitcoin carries risk, including
            loss of value and loss of access.
          </p>
          <p className="gybtc-footer-text">
            © {year} GrowYourBTC{' • '}
            <Link to="/privacy">Privacy Policy</Link>{' • '}
            <Link to="/terms">Terms</Link>{' • '}
            <Link to="/disclaimer">Disclaimer</Link>{' • '}
            <Link to="/faq">FAQ</Link>
          </p>
        </div>
      </footer>

      <ContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </div>
  );
};

export default Home;
